import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Field, FieldLabel } from '@/components/ui/field'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AdminController, AdminUser } from '../../controllers/admin'
import UserLookup from '../components/UserLookup'

export default function DeleteUserPage() {
  const { t } = useTranslation()
  const [user, setUser] = React.useState<AdminUser | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [confirmation, setConfirmation] = React.useState('')

  const loadUser = async (id: string) => {
    setLoading(true)
    setUser(null)
    try {
      setUser(await AdminController.getUser(id))
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const openConfirm = () => {
    setConfirmation('')
    setConfirmOpen(true)
  }

  const destroy = async () => {
    setLoading(true)
    try {
      await AdminController.deleteUser(user.id, confirmation)
      toast.success(t('admin::Deletion of {{email}} queued', { email: user.email, interpolation: { escapeValue: false } }))
      setConfirmOpen(false)
      setUser(null)
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const confirmed = user && confirmation.trim().toLowerCase() === user.email.trim().toLowerCase()
  const ownedWorkspaces = user ? user.workspaces.filter((workspace) => workspace.owned) : []

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold">{t('admin::Delete user')}</h1>
      <UserLookup loading={loading} onSelect={loadUser} />

      {user && (
        <Card>
          <CardHeader>
            <CardTitle>{user.email}</CardTitle>
            <CardDescription>{user.name}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <ul className="flex flex-col gap-2">
              {user.workspaces.map((workspace) => (
                <li key={workspace.id} className="flex items-center gap-2 text-sm">
                  <span>{workspace.business_name}</span>
                  {workspace.owned && <Badge variant="destructive">{t('admin::Will be deleted')}</Badge>}
                </li>
              ))}
            </ul>
            {user.admin ? (
              <p className="text-sm text-muted-foreground">{t('admin::Admin users cannot be deleted here.')}</p>
            ) : (
              <Button variant="destructive" className="self-start" onClick={openConfirm} disabled={loading}>
                {t('admin::Delete user')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin::Delete {{email}}?', { email: user?.email, interpolation: { escapeValue: false } })}</DialogTitle>
            <DialogDescription>
              {ownedWorkspaces.length > 0
                ? t('admin::This permanently deletes the user and these workspaces: {{names}}.', {
                    names: ownedWorkspaces.map((workspace) => workspace.business_name).join(', '),
                  interpolation: { escapeValue: false },
                  })
                : t('admin::This permanently deletes the user.')}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="admin-delete-confirmation">{t('admin::Type the email address to confirm')}</FieldLabel>
            <Input
              id="admin-delete-confirmation"
              autoComplete="off"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>{t('admin::Cancel')}</DialogClose>
            <Button variant="destructive" onClick={destroy} disabled={!confirmed || loading}>
              {t('admin::Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
